import { GoogleMedia } from '@/model/googlemedia.model';
import { Program } from '@/model/program.model';
import { ProgramRelease } from '@/model/programrelease.model';
import { libraryMusicService } from '@/services/library-music.service';
import { releaseEffectiveHasAccess } from '@/utils/release-catalog-privilege';
import store from '@/store';
import { CapacitorHttp } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { isPlatform } from '@ionic/vue';
import axios from 'axios';

export const LOCKED_TRACK_ALERT_TITLE = 'Нет доступа';
export const LOCKED_TRACK_ALERT_MESSAGE =
  'Этот трек из релиза, который ещё не куплен. Купите релиз, чтобы слушать его в миксе.';

export type PlaylistTrackSource = 'release' | 'library';

export function formatPlaylistReleaseName(releaseName: string | undefined): string {
  if (!releaseName) return '';
  return releaseName.length > 1 ? releaseName : `0${releaseName}`;
}

export function findReleaseInProgram(
  program: Program | null | undefined,
  releaseNameFormatted: string
): ProgramRelease | undefined {
  if (!program?.fullReleases?.length || !releaseNameFormatted) return undefined;
  return program.fullReleases.find((r) => {
    const withZero = formatPlaylistReleaseName(r.releaseName);
    return withZero === releaseNameFormatted || r.releaseName === releaseNameFormatted;
  });
}

export function getAccessibleReleases(
  program: Program | null | undefined,
  account: { authorities?: unknown } | null | undefined = store.getters.account
): ProgramRelease[] {
  if (!program?.fullReleases?.length) return [];
  return program.fullReleases.filter((r) => releaseEffectiveHasAccess(r, account));
}

export function isPlaylistTrackLocked(
  track: GoogleMedia | null | undefined,
  accessibleTrackIds: Set<string> | Record<string, boolean>,
  program?: Program | null,
  account: { authorities?: unknown } | null | undefined = store.getters.account
): boolean {
  if (!track?.id) return false;
  if (track.source === 'library' || track.id.startsWith('library_music/')) {
    return false;
  }
  if (track.releaseId != null && program?.fullReleases?.length) {
    const release = program.fullReleases.find((r) => r.id === track.releaseId);
    if (release) {
      return !releaseEffectiveHasAccess(release, account);
    }
  }
  const id = track.id;
  if (accessibleTrackIds instanceof Set) {
    return !accessibleTrackIds.has(id);
  }
  return !accessibleTrackIds[id];
}

export function playablePlaylistTracks(
  tracks: GoogleMedia[] | undefined,
  accessibleTrackIds: Set<string> | Record<string, boolean>,
  program?: Program | null
): GoogleMedia[] {
  if (!tracks?.length) return [];
  return tracks.filter((t) => !isPlaylistTrackLocked(t, accessibleTrackIds, program));
}

export function stampReleaseTrack(track: GoogleMedia, releaseId: number): GoogleMedia {
  return Object.assign(track, { source: 'release' as PlaylistTrackSource, releaseId });
}

export function stampLibraryTrack(track: GoogleMedia): GoogleMedia {
  return Object.assign(track, { source: 'library' as PlaylistTrackSource, releaseId: undefined });
}

async function fetchReleaseMusicList(
  program: Program,
  release: { id?: number; releaseName?: string; contentSource?: string }
): Promise<GoogleMedia[]> {
  if (release.contentSource === 'SERVER' && release.id) {
    if (isPlatform('ios') || isPlatform('android')) {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const response = await CapacitorHttp.get({
        url: `${process.env.VUE_APP_API_URL}api/releases/${release.id}/media/music`,
        headers: { Authorization: `Bearer ${tokenQuery.value}` },
        readTimeout: 60000,
      });
      return Array.isArray(response.data) ? response.data : [];
    }
    const response = await axios.get(`/api/releases/${release.id}/media/music`);
    return Array.isArray(response.data) ? response.data : [];
  }

  const releaseNn = formatPlaylistReleaseName(release.releaseName);
  const path = `${program.shortName}_${releaseNn}_Music`;
  if (isPlatform('ios') || isPlatform('android')) {
    const tokenQuery = await Preferences.get({ key: 'jwt' });
    const response = await CapacitorHttp.get({
      url: `${process.env.VUE_APP_API_URL}api/google/media/${path}`,
      headers: { Authorization: `Bearer ${tokenQuery.value}` },
      readTimeout: 60000,
    });
    if (response.status === 403) return [];
    return Array.isArray(response.data) ? response.data : [];
  }
  try {
    const response = await axios.get(`/api/google/media/${path}`);
    return Array.isArray(response.data) ? response.data : [];
  } catch {
    return [];
  }
}

/** Собирает id треков, доступных пользователю (доступные релизы программы + library). */
export async function collectAccessibleTrackIds(
  program: Program | null | undefined,
  selectedTracks: GoogleMedia[] = [],
  account: { authorities?: unknown } | null | undefined = store.getters.account
): Promise<Record<string, boolean>> {
  const ids: Record<string, boolean> = {};
  if (!program) return ids;

  for (const release of getAccessibleReleases(program, account)) {
    try {
      const list = await fetchReleaseMusicList(program, release);
      for (const t of list) {
        if (t?.id) ids[t.id] = true;
      }
    } catch (e) {
      console.warn('collectAccessibleTrackIds release failed', release.id, e);
    }
  }

  try {
    const folders = await libraryMusicService.listUserFolders();
    for (const folder of folders) {
      try {
        const tracks = await libraryMusicService.listUserTracks(folder.id);
        for (const t of tracks) {
          if (t?.id) ids[t.id] = true;
        }
      } catch {
        /* folder without tracks */
      }
    }
  } catch (e) {
    console.warn('collectAccessibleTrackIds library failed', e);
  }

  for (const t of selectedTracks) {
    if (t.source === 'library' || t.id?.startsWith('library_music/')) {
      if (t.id) ids[t.id] = true;
    }
    if (t.releaseId != null) {
      const rel = program.fullReleases?.find((r) => r.id === t.releaseId);
      if (rel && releaseEffectiveHasAccess(rel, account) && t.id) {
        ids[t.id] = true;
      }
    }
  }

  return ids;
}
